(function (global) {
    'use strict';

    const LEGACY_STATE_KEYS = ['search_products', 'search_choices', 'products', 'choices'];
    const CHOICE_STATE_KEYS = new Set([
        'page',
        'location',
        'custom',
        'sale',
        'category',
        'pattern',
        'construction',
        'shape',
        'design',
        'size',
        'colour',
        'fabric',
        'inputs',
        'anyColourMatch',
        'showPrimaryColoursItemsOnly'
    ]);
    const SIZE_INPUT_KEYS = new Set(['min_length', 'max_length', 'min_width', 'max_width']);

    function normaliseQuery(query) {
        return String(query == null ? '' : query)
            .trim()
            .replace(/\s+/g, ' ')
            .toLowerCase();
    }

    function pageNumber(payload) {
        const value = Number(payload && payload.current_page);
        return Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
    }

    function productIdentity(product) {
        if (!product || typeof product !== 'object' || Array.isArray(product)) return null;
        if (product.id != null) return `id:${String(product.id)}`;
        if (product.handle != null) return `handle:${String(product.handle)}`;
        return null;
    }

    function flattenRelevancePages(pagePayloads, rankProperty = 'relevanceRank') {
        const products = [];
        const seenKeys = new Set();
        let relevanceRank = 0;

        (Array.isArray(pagePayloads) ? pagePayloads : [])
            .slice()
            .sort((left, right) => pageNumber(left) - pageNumber(right))
            .forEach(payload => {
                if (!payload || !Array.isArray(payload.products)) return;

                payload.products.forEach(product => {
                    if (!product || typeof product !== 'object' || Array.isArray(product)) return;

                    const key = productIdentity(product);
                    if (key && seenKeys.has(key)) return;
                    if (key) seenKeys.add(key);

                    const copy = {...product};
                    Object.defineProperty(copy, rankProperty, {
                        configurable: false,
                        enumerable: true,
                        value: relevanceRank++,
                        writable: false
                    });
                    products.push(copy);
                });
            });

        return products;
    }

    function mergeMappedProductsWithOriginalRanks(
        mappedPages,
        rankPages,
        rankProperty = 'relevanceRank'
    ) {
        const mappedProducts = flattenRelevancePages(mappedPages, 'mappedQueryRank');
        const originalProducts = flattenRelevancePages(rankPages, 'originalQueryRank');
        const originalRanks = new Map();

        originalProducts.forEach((product, index) => {
            const key = productIdentity(product);
            if (key && !originalRanks.has(key)) originalRanks.set(key, index);
        });

        let fallbackRank = originalProducts.length;
        return mappedProducts
            .map(product => {
                const key = productIdentity(product);
                const originalRank = key ? originalRanks.get(key) : undefined;
                const relevanceRank = originalRank == null
                    ? fallbackRank++
                    : originalRank;
                return {...product, [rankProperty]: relevanceRank};
            })
            .sort((left, right) => left[rankProperty] - right[rankProperty]);
    }

    function numericValue(value) {
        if (typeof value === 'number') return Number.isFinite(value) ? value : null;
        if (typeof value !== 'string') return null;

        const number = Number(value.replace(/[^0-9.-]/g, ''));
        return Number.isFinite(number) ? number : null;
    }

    function productPrice(product) {
        return productMetrics(product).startingPrice;
    }

    function productHighestPrice(product) {
        return productMetrics(product).highestPrice;
    }

    function productDate(product) {
        const raw = product && (product.date_added ?? product.created_at ?? product.published_at);
        const time = raw == null ? NaN : Date.parse(String(raw));
        return Number.isFinite(time) ? time : null;
    }

    function productMetrics(product) {
        if (global.LrCollectionFilters && typeof global.LrCollectionFilters.productSortMetrics === 'function') {
            return global.LrCollectionFilters.productSortMetrics(product);
        }
        return {
            startingPrice: null,
            highestPrice: null,
            discountPercent: 0,
            discountAmount: 0,
            minArea: null,
            maxArea: null
        };
    }

    function productTitle(product) {
        return String(product && product.title != null ? product.title : '');
    }

    function compareNumbers(left, right, descending) {
        if (global.LrCollectionFilters && typeof global.LrCollectionFilters.compareSortValues === 'function') {
            return global.LrCollectionFilters.compareSortValues(left, right, descending);
        }
        if (left == null && right == null) return 0;
        if (left == null) return 1;
        if (right == null) return -1;
        const comparison = left - right;
        return descending ? -comparison : comparison;
    }

    function compareRelevance(left, right) {
        const leftRank = numericValue(left && left.relevanceRank);
        const rightRank = numericValue(right && right.relevanceRank);
        return compareNumbers(leftRank, rightRank, false);
    }

    function compareProducts(left, right, sortBy) {
        const sort = String(sortBy || 'relevance').toLowerCase();

        if (sort === 'relevance') return compareRelevance(left, right);
        if (sort === 'low-to-high' || sort === 'price-ascending' || sort === 'price-asc') {
            return compareNumbers(productPrice(left), productPrice(right), false);
        }
        if (sort === 'high-to-low' || sort === 'price-descending' || sort === 'price-desc') {
            return compareNumbers(productHighestPrice(left), productHighestPrice(right), true);
        }
        if (sort === 'title-ascending' || sort === 'title-asc' || sort === 'title') {
            return productTitle(left).localeCompare(productTitle(right), undefined, {sensitivity: 'base'});
        }
        if (sort === 'title-descending' || sort === 'title-desc') {
            return productTitle(right).localeCompare(productTitle(left), undefined, {sensitivity: 'base'});
        }
        if (sort === 'new' || sort === 'newest' || sort === 'created-descending') {
            return compareNumbers(productDate(left), productDate(right), true);
        }
        if (sort === 'discount-high-to-low') {
            return compareNumbers(productMetrics(left).discountPercent, productMetrics(right).discountPercent, true);
        }
        if (sort === 'discount-amount-high-to-low') {
            return compareNumbers(productMetrics(left).discountAmount, productMetrics(right).discountAmount, true);
        }
        if (sort === 'size-large-small') {
            return compareNumbers(productMetrics(left).maxArea, productMetrics(right).maxArea, true);
        }
        if (sort === 'size-small-large') {
            return compareNumbers(productMetrics(left).minArea, productMetrics(right).minArea, false);
        }

        return 0;
    }

    function sortSearchProducts(products, sortBy) {
        return (Array.isArray(products) ? products : [])
            .map((product, index) => ({product, index}))
            .sort((left, right) => compareProducts(left.product, right.product, sortBy) || left.index - right.index)
            .map(entry => entry.product);
    }

    function isPlainObject(value) {
        return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
    }

    function sanitisePrimitive(value) {
        if (value == null || typeof value === 'string' || typeof value === 'boolean') return value;
        return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
    }

    function sanitiseChoiceValue(value, key) {
        if (key === 'inputs') {
            if (!isPlainObject(value)) return undefined;

            const inputs = {};
            SIZE_INPUT_KEYS.forEach(inputKey => {
                const input = sanitisePrimitive(value[inputKey]);
                if (input !== undefined) inputs[inputKey] = input;
            });
            return inputs;
        }

        if (Array.isArray(value)) {
            return value
                .map(sanitisePrimitive)
                .filter(item => item !== undefined);
        }
        return sanitisePrimitive(value);
    }

    function sanitiseChoices(value) {
        if (!isPlainObject(value)) return undefined;

        const choices = {};
        CHOICE_STATE_KEYS.forEach(key => {
            const choice = sanitiseChoiceValue(value[key], key);
            if (choice !== undefined) choices[key] = choice;
        });
        return choices;
    }

    function sanitiseSearchState(state) {
        if (!isPlainObject(state)) return {};

        const clean = {};
        if (typeof state.sortBy === 'string') clean.sortBy = state.sortBy;

        const choices = sanitiseChoices(state.choices);
        if (choices !== undefined) clean.choices = choices;

        return clean;
    }

    function searchStateKey(query) {
        return `lrSearchState:${normaliseQuery(query)}:v1`;
    }

    function readSearchState(storage, query) {
        const normalizedQuery = normaliseQuery(query);
        if (!storage || !normalizedQuery) return null;

        try {
            const raw = storage.getItem(searchStateKey(normalizedQuery));
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
                ? sanitiseSearchState(parsed)
                : null;
        } catch (error) {
            return null;
        }
    }

    function writeSearchState(storage, query, state) {
        const normalizedQuery = normaliseQuery(query);
        if (!storage || !normalizedQuery) return;

        try {
            storage.setItem(searchStateKey(normalizedQuery), JSON.stringify(sanitiseSearchState(state)));
        } catch (error) {
            // Storage may be unavailable or full; search state is optional.
        }
    }

    function clearLegacySearchState(storage) {
        if (!storage) return;
        LEGACY_STATE_KEYS.forEach(key => {
            try {
                storage.removeItem(key);
            } catch (error) {
                // Storage cleanup is best effort.
            }
        });
    }

    function buildSearchFeedContext({originalQuery, refinement, currentUrl}) {
        const pageUrl = new URL(currentUrl, 'https://www.love-rugs.com/search');
        const broadSearchRequested = pageUrl.searchParams.get('lr_broad') === '1';
        const constrained = Boolean(refinement && refinement.constrained && !broadSearchRequested);
        const broaderResultsUrl = new URL(pageUrl.toString());
        broaderResultsUrl.searchParams.set('lr_broad', '1');

        return {
            query: constrained ? refinement.query : String(originalQuery == null ? '' : originalQuery),
            constrained,
            broaderResultsUrl: broaderResultsUrl.toString()
        };
    }

    function searchFeedUiState({status, constrained, productCount}) {
        if (status === 'success') {
            return {
                fetchingProducts: false,
                clientGridActive: Boolean(constrained),
                emptyExactResults: Boolean(constrained && Number(productCount) === 0),
                completeResultsAvailable: true,
                searchResultsError: false
            };
        }

        return {
            fetchingProducts: false,
            clientGridActive: false,
            emptyExactResults: false,
            completeResultsAvailable: false,
            searchResultsError: true
        };
    }

    const api = {
        normaliseQuery,
        productIdentity,
        flattenRelevancePages,
        mergeMappedProductsWithOriginalRanks,
        sortSearchProducts,
        readSearchState,
        writeSearchState,
        clearLegacySearchState,
        buildSearchFeedContext,
        searchFeedUiState
    };

    global.LrSearchResults = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
